import React, {useCallback} from "react";
import styled from "@emotion/styled";
import {IconButton, InputAdornment, MenuItem} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {getPlayerPoolFilters, getSquads, getStats} from "modules/selectors";
import {updatePlayerPoolFilters} from "modules/actions";
import {IPlayerPoolFilters} from "modules/types";
import {POSITIONS_ARRAY} from "modules/constants";
import {getIsDisabledStatByPosition, getSquadKey} from "modules/utils";
import {PlayerPositionEnum} from "modules/enums";
import {Input, Select} from "components/Common";
import {ReactComponent as SearchIcon} from "assets/images/icons/search.svg";

const Wrapper = styled.div`
	padding: 16px 20px;
	background: ${({theme}) => theme.colorPalette.black};
	border-bottom: 1px solid #cccccc;
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

const Filters = styled.div`
	display: flex;
	gap: 20px;
`;

const locoKeyAll = "player_pool.filters.all";

export const PlayerPoolFilters: React.FC = () => {
	const {t} = useTranslation();
	const dispatch = useDispatch();
	const filters = useSelector(getPlayerPoolFilters);
	const positions = POSITIONS_ARRAY;
	const stats = useSelector(getStats);
	const squads = useSelector(getSquads);

	const onChangeFilter = useCallback(
		(type: keyof IPlayerPoolFilters) => (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;

			dispatch(
				updatePlayerPoolFilters({
					...filters,
					[type]: value,
				})
			);
		},
		[dispatch, filters]
	);

	const onChangePositionFilter = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value as PlayerPositionEnum | number;

			dispatch(
				updatePlayerPoolFilters({
					...filters,
					position: value,
					stats: value ? 0 : filters.stats,
				})
			);
		},
		[dispatch, filters]
	);

	return (
		<Wrapper>
			<Filters>
				<Input
					className="search"
					type="text"
					name="name"
					label={t("player_pool.filters.search")}
					placeholder={t("player_pool.filters.search_placeholder")}
					fullWidth
					value={filters.value}
					onChange={onChangeFilter("value")}
					autoComplete="off"
					InputProps={{
						endAdornment: (
							<InputAdornment position="end">
								<IconButton edge="end">
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Filters>
			<Filters>
				<Select
					label={t("player_pool.filters.team")}
					fullWidth
					value={filters.team}
					onChange={onChangeFilter("team")}>
					<MenuItem value={0}>{t(locoKeyAll)}</MenuItem>
					{squads.map((squad) => (
						<MenuItem key={squad.id} value={squad.id}>
							{t(getSquadKey(squad))}
						</MenuItem>
					))}
				</Select>

				<Select
					label={t("player_pool.filters.position")}
					fullWidth
					value={filters["position"]}
					onChange={onChangePositionFilter}>
					<MenuItem value={0}>{t(locoKeyAll)}</MenuItem>
					{positions.map(({value, name}) => (
						<MenuItem key={value} value={value}>
							{t(`player_pool.filters.${name.toLowerCase()}`)}
						</MenuItem>
					))}
				</Select>
			</Filters>
			<Filters>
				<Select
					label={t("player_pool.filters.stats")}
					fullWidth
					value={filters["stats"]}
					onChange={onChangeFilter("stats")}>
					<MenuItem value={0}>{t(locoKeyAll)}</MenuItem>
					{stats.map((stat) => (
						<MenuItem
							key={stat.id}
							value={stat.id}
							disabled={getIsDisabledStatByPosition(filters.position, stat.id)}>
							{t(stat.name)}
						</MenuItem>
					))}
				</Select>
			</Filters>
		</Wrapper>
	);
};
